import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, CardHeader, ToggleButton, ToggleButtonGroup,
  Button, Alert, CircularProgress, GlobalStyles, Divider, Link,
} from '@mui/material';
import { Print as PrintIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useSettings } from '../contexts/SettingsContext';
import KpiTile from '../components/Reports/KpiTile';
import TopTable, { MitreLink } from '../components/Reports/TopTable';
import MitreHeatmap from '../components/Reports/MitreHeatmap';
import { StackedBarChart, LineChart, DonutChart, BarList, useCategoricalColors, foldOther } from '../components/Reports/charts';

const SEVERITIES = ['Critical', 'High', 'Medium', 'Low'];
const RANGES = [7, 30, 90];

const fmtMinutes = (m) => {
  if (m == null || Number.isNaN(m)) return '—';
  const total = Math.round(m);
  if (total < 60) return `${total}m`;
  const h = Math.floor(total / 60); const mm = total % 60;
  if (h < 48) return mm ? `${h}h ${mm}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
};
const pct = (v) => (v == null ? '—' : `${v}%`);

const printStyles = (
  <GlobalStyles styles={{
    '@media print': {
      '.MuiAppBar-root, .MuiDrawer-root, .no-print': { display: 'none !important' },
      main: { marginLeft: '0 !important', padding: '0 !important', width: '100% !important' },
      body: { backgroundColor: '#fff' },
      '.MuiCard-root': { breakInside: 'avoid', boxShadow: 'none' },
      '.MuiToolbar-root': { display: 'none' },
    },
  }} />
);

const Section = ({ title, subheader, children, ...props }) => (
  <Card variant="outlined" sx={{ height: '100%' }} {...props}>
    <CardHeader title={title} subheader={subheader} titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} sx={{ pb: 0 }} />
    <CardContent>{children}</CardContent>
  </Card>
);

const Reports = () => {
  const { getSeverityColor } = useSettings();
  const { colors: catColors, other: otherColor } = useCategoricalColors();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mitreData, setMitreData] = useState(null);
  const [mitreLoading, setMitreLoading] = useState(true);
  const [mitreError, setMitreError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await apiService.reports.getSummary(days);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [days]);

  const loadMitre = useCallback(async () => {
    setMitreLoading(true); setMitreError(null);
    try {
      const res = await apiService.reports.getMitre(days);
      setMitreData(res.data);
    } catch (err) {
      setMitreError(err.response?.data?.message || err.message || 'Failed to load ATT&CK coverage');
    } finally {
      setMitreLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMitre(); }, [loadMitre]);

  useEffect(() => {
    apiService.users.getAll({ limit: 100 })
      .then((res) => setUsers(res.data?.users || []))
      .catch(() => setUsers([]));
  }, []);

  const userName = useMemo(() => {
    const map = {};
    for (const u of users) map[u._id] = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || u._id;
    return (id) => map[id] || `Unknown (${String(id).slice(-6)})`;
  }, [users]);

  const topEntityRows = useMemo(
    () => (data?.topEntities || []).map((e) => ({ ...e, count: e.riskScore ?? 0 })),
    [data]
  );

  const severityKeys = useMemo(() => SEVERITIES.map((s) => ({ key: s, label: s, color: getSeverityColor(s).main })), [getSeverityColor]);
  const categoricalColor = useCallback((d, i) => (d.other ? otherColor : catColors[i % catColors.length]), [catColors, otherColor]);
  const severityColorFor = useCallback((d) => getSeverityColor(d.name).main, [getSeverityColor]);

  const totals = data?.totals || {};
  const isEmpty = data && !totals.detections && !totals.casesCreated && !totals.casesResolved && !totals.openCases;
  const fpRate = totals.detections ? Math.round((totals.falsePositives / totals.detections) * 100) : 0;
  const sinceLabel = data?.range?.since ? new Date(data.range.since).toLocaleDateString() : '';

  return (
    <Box>
      {printStyles}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" gutterBottom>Reports</Typography>
          <Typography variant="body1" color="textSecondary">
            SOC operational summary{sinceLabel ? ` · since ${sinceLabel}` : ''} · last {days} days
          </Typography>
        </Box>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" className="no-print">
          <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)} aria-label="Time range">
            {RANGES.map((r) => <ToggleButton key={r} value={r}>{r} days</ToggleButton>)}
          </ToggleButtonGroup>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => { load(); loadMitre(); }} disabled={loading}>Refresh</Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()} disabled={!data}>Print / PDF</Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>{error}</Alert>
      )}

      {loading && !data && (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      )}

      {data && isEmpty && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No detections or cases were recorded in the last {days} days. Ingest detections or widen the time range to populate this report.
        </Alert>
      )}

      {data && (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 150ms' }}>
          {/* KPI tiles */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} sm={4} md={2}><KpiTile label="Detections" value={totals.detections ?? 0} sub={`${totals.openDetections ?? 0} open`} /></Grid>
            <Grid item xs={6} sm={4} md={2}><KpiTile label="Critical" value={totals.criticalDetections ?? 0} color={getSeverityColor('Critical').main} sub="critical severity" /></Grid>
            <Grid item xs={6} sm={4} md={2}><KpiTile label="False-positive rate" value={pct(fpRate)} sub={`${totals.falsePositives ?? 0} marked`} /></Grid>
            <Grid item xs={6} sm={4} md={2}><KpiTile label="Cases created" value={totals.casesCreated ?? 0} sub={`${totals.openCases ?? 0} open overall`} /></Grid>
            <Grid item xs={6} sm={4} md={2}><KpiTile label="Cases resolved" value={totals.casesResolved ?? 0} sub="in range" /></Grid>
            <Grid item xs={6} sm={4} md={2}><KpiTile label="Ingested" value={`${totals.ingestedAlerts ?? 0} → ${totals.ingestedDetections ?? 0}`} sub="alerts → detections" /></Grid>
            <Grid item xs={6} sm={4} md={3}><KpiTile label="MTTA" value={fmtMinutes(totals.mttaMinutes)} sub="mean time to acknowledge" /></Grid>
            <Grid item xs={6} sm={4} md={3}><KpiTile label="MTTR" value={fmtMinutes(totals.mttrMinutes)} sub="mean time to resolve" /></Grid>
            <Grid item xs={6} sm={4} md={3}><KpiTile label="SLA compliance" value={pct(totals.slaCompliance)} sub={totals.slaCompliance == null ? 'no SLA-tracked cases' : `${totals.slaBreached ?? 0} breached`} /></Grid>
            <Grid item xs={6} sm={4} md={3}><KpiTile label="AI coverage" value={pct(totals.aiCoverage)} sub="detections analysed by AI" /></Grid>
          </Grid>

          {/* Time series */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={7}>
              <Section title="Daily detections by severity">
                {totals.detections ? <StackedBarChart series={data.series} keys={severityKeys} /> : <Typography variant="body2" color="text.secondary">No detections in this range.</Typography>}
              </Section>
            </Grid>
            <Grid item xs={12} md={5}>
              <Section title="Cases created vs resolved">
                {(totals.casesCreated || totals.casesResolved) ? (
                  <LineChart
                    series={data.series}
                    ariaLabel="Cases created versus resolved per day"
                    keys={[{ key: 'casesCreated', label: 'Created', color: catColors[0] }, { key: 'casesResolved', label: 'Resolved', color: catColors[2] }]}
                  />
                ) : <Typography variant="body2" color="text.secondary">No case activity in this range.</Typography>}
              </Section>
            </Grid>
          </Grid>

          {/* Breakdowns */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={6} lg={3}>
              <Section title="Detections by source">
                {data.detectionsBySource?.length ? <DonutChart data={foldOther(data.detectionsBySource)} colorFor={categoricalColor} ariaLabel="Detections by source" /> : <Typography variant="body2" color="text.secondary">No data.</Typography>}
              </Section>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Section title="Detections by status">
                {data.detectionsByStatus?.length ? <DonutChart data={foldOther(data.detectionsByStatus)} colorFor={categoricalColor} ariaLabel="Detections by status" /> : <Typography variant="body2" color="text.secondary">No data.</Typography>}
              </Section>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Section title="Detections by category">
                {data.detectionsByCategory?.length ? <BarList data={foldOther(data.detectionsByCategory)} colorFor={categoricalColor} ariaLabel="Detections by category" /> : <Typography variant="body2" color="text.secondary">No data.</Typography>}
              </Section>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Section title="Detections by vendor / agent">
                {data.detectionsByVendor?.length ? <BarList data={foldOther(data.detectionsByVendor)} colorFor={categoricalColor} ariaLabel="Detections by vendor" /> : <Typography variant="body2" color="text.secondary">No data.</Typography>}
              </Section>
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={6}>
              <Section title="Cases by severity">
                {data.casesBySeverity?.length ? <BarList data={data.casesBySeverity} colorFor={severityColorFor} ariaLabel="Cases by severity" /> : <Typography variant="body2" color="text.secondary">No cases in this range.</Typography>}
              </Section>
            </Grid>
            <Grid item xs={12} md={6}>
              <Section title="Cases by category">
                {data.casesByCategory?.length ? <BarList data={foldOther(data.casesByCategory)} colorFor={categoricalColor} ariaLabel="Cases by category" /> : <Typography variant="body2" color="text.secondary">No cases in this range.</Typography>}
              </Section>
            </Grid>
          </Grid>

          {/* MITRE ATT&CK coverage */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12}>
              <Section title="MITRE ATT&CK coverage" subheader="techniques observed across detections in this range">
                {mitreError ? (
                  <Alert severity="error" action={<Button color="inherit" size="small" onClick={loadMitre}>Retry</Button>}>{mitreError}</Alert>
                ) : mitreLoading && !mitreData ? (
                  <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>
                ) : (
                  <MitreHeatmap data={mitreData} />
                )}
              </Section>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          {/* Tables */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={3}>
              <Section title="Top hosts" subheader="by detection count">
                <TopTable rows={data.topHosts} nameLabel="Host" countLabel="Detections" />
              </Section>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Section title="Top users" subheader="by detection count">
                <TopTable rows={data.topUsers} nameLabel="User" countLabel="Detections" />
              </Section>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Section title="Top MITRE ATT&CK techniques">
                <TopTable rows={data.topTechniques} nameLabel="Technique" countLabel="Detections" renderName={(r) => <MitreLink technique={r.name} />} />
              </Section>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Section title="Analyst load" subheader="open cases per assignee">
                <TopTable rows={data.analystLoad} nameLabel="Analyst" countLabel="Open cases" renderName={(r) => userName(r.name)} emptyText="No open cases are assigned." />
              </Section>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Section title="Highest-risk assets" subheader="by current risk score">
                <TopTable
                  rows={topEntityRows}
                  nameLabel="Asset"
                  countLabel="Risk"
                  emptyText="No entities have a risk score yet."
                  renderName={(r) => (
                    <Box sx={{ minWidth: 0 }}>
                      <Link component={RouterLink} to={`/dashboard/entities/${r._id}`} underline="hover">{r.name}</Link>
                      <Typography variant="caption" color="text.secondary" display="block">{r.type}</Typography>
                    </Box>
                  )}
                />
              </Section>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default Reports;
